import React from 'react';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { APPLICATION_COMMENTS_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { drawScopedKey } from './draw-scoped-sections.util';

interface ApplicationCommentsSectionProps {
  magicReportId: string;
  /** The draw this copy of the section belongs to. */
  drawId: string;
}

/**
 * Application Comments Section Content
 * Using declarative config-driven rendering
 */
const ApplicationCommentsSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const commentsField = getCommentsField(data, APPLICATION_COMMENTS_SECTION_CONFIG.config);

  if (!APPLICATION_COMMENTS_SECTION_CONFIG.layout) {
    return <div>Layout configuration missing</div>;
  }

  // Handle field deletion (clear content)
  const handleDeleteField = (fieldName: string) => {
    handleFieldChange(fieldName, { value: '', addToActionItems: false });
  };

  return (
    <>
      {/* Config-driven layout rendering */}
      <DeclarativeLayoutRenderer
        layout={APPLICATION_COMMENTS_SECTION_CONFIG.layout}
        getField={getField}
        handleFieldChange={handleFieldChange}
        onDeleteField={handleDeleteField}
        onBook={onBook}
      />

      {/* Comments Section */}
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange('Comments', updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </>
  );
};

/**
 * Application Comments Section
 * Fully config-driven! Layout is defined in sections.config.ts
 */
export const ApplicationCommentsSection: React.FC<ApplicationCommentsSectionProps> = ({ magicReportId, drawId }) => {
  // Each draw gets its own section id, so MagicReportSection stores, anchors and completes this
  // draw's comments independently — no changes needed inside the shared section component.
  const sectionConfig = React.useMemo(
    () => ({ ...APPLICATION_COMMENTS_SECTION_CONFIG, id: drawScopedKey(APPLICATION_COMMENTS_SECTION_CONFIG.id, drawId) }),
    [drawId],
  );

  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={sectionConfig}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ApplicationCommentsSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
